import React, { useRef, useState, useEffect } from 'react';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedPhone } from '../../../store/whatsappSlice.js';
import { SendFromSelector } from './SendFromSelector.jsx';
import { RecipientSelectionCard } from './RecipientSelectionCard.jsx';
import { parseContactImportFile } from '../../../pages/whatsapp/utils/contactsImportUtils';

export const RecipientInput = ({
  manualNumbers,
  onManualChange,
  importedCount,
  importSource,
  onImportCsv,
  onClearImport,
  templateVariables = [],
  selectedTemplate
}) => {
  const dispatch = useDispatch();
  const { phones = [], selectedPhone } = useSelector(state => state.whatsapp || {});

  const fileInputRef = useRef(null);
  const fileInputRefExcel = useRef(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importError, setImportError] = useState('');

  const manualMode = manualNumbers && manualNumbers.trim().length > 0;
  const csvActive = importSource === 'CSV' && importedCount > 0;
  const excelActive = importSource === 'EXCEL' && importedCount > 0;

  const manualCount = manualNumbers ? manualNumbers.split(/[,\n]/).filter(n => n.trim()).length : 0;
  const totalCount = (importedCount && importedCount > 0) ? importedCount : manualCount;

  const [selectedSender, setSelectedSender] = useState(selectedPhone ? String(selectedPhone.id) : '');

  useEffect(() => {
    dispatch({ type: 'FETCH_WABA_IDS' });
  }, [dispatch]);

  useEffect(() => {
    setSelectedSender(selectedPhone ? String(selectedPhone.id) : '');
  }, [selectedPhone]);

  useEffect(() => {
    if (!selectedSender) return;
    const phoneObj = phones.find(p => String(p.id) === String(selectedSender)) || null;
    if (phoneObj) {
      dispatch(setSelectedPhone(phoneObj));
    }
  }, [selectedSender, phones, dispatch]);

  const validatePhoneNumbers = (text) => {
    const numbers = text.split(/[,\n]/).map(n => n.trim()).filter(n => n);
    if (numbers.length > 1) {
      return ['multiple_numbers'];
    }
    const invalid = numbers.filter(num => !isValidPhoneNumber(num));
    return invalid;
  };

  const handleManualChange = (value) => {
    onManualChange(value);
    const invalids = validatePhoneNumbers(value);
    setValidationErrors(invalids);
  };

  const handleImportCsv = () => {
    if (manualMode) {
      setImportError('Please clear manual numbers before importing a CSV.');
      return;
    }
    if (excelActive) {
      setImportError('Please clear Excel import before importing a CSV.');
      return;
    }
    fileInputRef.current.click();
  };

  const handleImportExcel = () => {
    if (manualMode) {
      setImportError('Please clear manual numbers before importing an Excel file.');
      return;
    }
    if (csvActive) {
      setImportError('Please clear CSV import before importing an Excel file.');
      return;
    }
    fileInputRefExcel.current.click();
  };

  const handleExcelFileChange = async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;

    if (manualMode) {
      setImportError('Please clear manual numbers before importing an Excel file.');
      return;
    }
    if (csvActive) {
      setImportError('A CSV import is active. Please clear the current import before selecting an Excel file.');
      return;
    }

    try {
      const parsed = await parseContactImportFile(file, 'excel');
      const fields = parsed.headers;
      const json = parsed.rows;

      const phoneCol = fields.find(f => f.toLowerCase().includes('phone') || f.toLowerCase().includes('number'));
      if (!phoneCol) {
        setImportError('Excel must contain a column with "phone" or "number" in the header.');
        return;
      }

      const phones = json.map(row => row[phoneCol]).filter(p => p && String(p).trim());
      if (phones.length === 0) {
        setImportError(`No valid phone numbers found in the "${phoneCol}" column.`);
        return;
      }

      const escapeCsv = (val) => {
        if (val === undefined || val === null) return '';
        const str = String(val);
        return `"${str.replace(/"/g, '""')}"`;
      };
      const csvHeader = fields.map(f => escapeCsv(f)).join(',');
      const csvRows = json.map(row => fields.map(f => escapeCsv(row[f] ?? '')).join(','));
      const csvContent = [csvHeader, ...csvRows].join('\n');

      onImportCsv(phones, csvContent, file.name, 'EXCEL', fields, json, phoneCol);
      setImportError('');
    } catch (err) {
      console.error('Excel parse error:', err);
      if (err && err.message) {
        setImportError(err.message);
      } else if (err && err.code === 'ERR_MODULE_NOT_FOUND') {
        setImportError('Excel parsing requires the "xlsx" package. Please install it (npm i xlsx) and try again.');
      } else {
        setImportError('Error parsing Excel file. Please ensure it is a valid .xlsx/.xls file.');
      }
    }
  };

  const handleClearImport = () => {
    setImportError('');
    onClearImport();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;

    if (manualMode) {
      setImportError('Please clear manual numbers before importing a CSV.');
      return;
    }
    if (excelActive) {
      setImportError('An Excel import is active. Please clear the current import before selecting a CSV file.');
      return;
    }

    try {
      const parsed = await parseContactImportFile(file, 'csv');
      const fields = parsed.headers;
      const data = parsed.rows;

      const phoneCol = fields.find(f => f.toLowerCase().includes('phone') || f.toLowerCase().includes('number'));
      if (!phoneCol) {
        setImportError('CSV must contain a column with "phone" or "number" in the header.');
        return;
      }
      const phones = data
        .map(row => row[phoneCol])
        .filter(phone => phone && String(phone).trim());
      if (phones.length === 0) {
        setImportError(`No valid phone numbers found in the "${phoneCol}" column.`);
        return;
      }

      const escapeCsv = (val) => {
        if (val === undefined || val === null) return '';
        const str = String(val);
        return `"${str.replace(/"/g, '""')}"`;
      };

      const csvHeader = fields.map(f => escapeCsv(f)).join(',');
      const csvRows = data.map(row => fields.map(f => escapeCsv(row[f] ?? '')).join(','));
      const csvContent = [csvHeader, ...csvRows].join('\n');

      onImportCsv(phones, csvContent, file.name, 'CSV', fields, data, phoneCol);
      setImportError('');
    } catch (error) {
      console.error('Error parsing CSV:', error);
      if (error && error.message) {
        setImportError(error.message);
      } else {
        setImportError('Error parsing CSV file. Please check the file format.');
      }
    }
  };

  return (
    <>
      <SendFromSelector
        phones={phones}
        selectedSender={selectedSender}
        onSenderChange={setSelectedSender}
      />

      <RecipientSelectionCard
        manualNumbers={manualNumbers}
        onManualChange={handleManualChange}
        validationErrors={validationErrors}
        manualMode={manualMode}
        csvActive={csvActive}
        excelActive={excelActive}
        onImportCsv={handleImportCsv}
        onImportExcel={handleImportExcel}
        totalCount={totalCount}
        importSource={importSource}
        importedCount={importedCount}
        importError={importError}
        onClearImport={handleClearImport}
      />

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv"
        style={{ display: 'none' }}
      />

      <input
        type="file"
        ref={fileInputRefExcel}
        onChange={handleExcelFileChange}
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
      />
    </>
  );
};